import React, { useState, useEffect } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { FaStar, FaInfoCircle, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api/api.js'; // Ensure this path is correct
import styles from './ImportGoogleRatingModal.module.css';


const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;


// --- HELPER FUNCTIONS for domain extraction ---
const getDomainFromEmail = (email) => {
    if (!email || !email.includes('@')) return null;
    return email.split('@').pop().toLowerCase();
};

const getDomainFromUrl = (url) => {
    if (!url) return null;
    try {
        const hostname = new URL(url).hostname;
        // Remove 'www.' if present for a cleaner comparison
        return hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    } catch (e) {
        console.error("Invalid URL for domain extraction:", url);
        return null;
    }
};


const ImportGoogleRatingModal = ({ isOpen, onClose, onImportSuccess, businessId }) => {
    
    const [view, setView] = useState('form');    
    
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [placeDetails, setPlaceDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [userEmailDomain, setUserEmailDomain] = useState('');

    // --- Get the user's email domain when the modal opens ---
    useEffect(() => {
        if (isOpen) {
            const loggedInEmail = localStorage.getItem('loggedInUsername');
            setUserEmailDomain(getDomainFromEmail(loggedInEmail));
        }
    }, [isOpen]);


    const handlePlaceSelect = async (selection) => {
        setError('');
        setPlaceDetails(null);
        setSelectedPlace(selection);

        if (!selection?.value?.place_id) {
            return;
        }

        setIsLoading(true);
        try {
            const placeId = selection.value.place_id;
            const detailsResponse = await api.post('/api/GooglePlaces/google-place-details', { placeId });

            var details = detailsResponse.data;

            console.log(details);

            if (details && details.status === "Success") {
                const businessWebsite = details.website;
                const businessWebsiteDomain = getDomainFromUrl(details.website);


               // 1. Check if the business has a website.
                if (!businessWebsiteDomain) {
                    setError({
                        title: "Verification Not Possible",
                        details: "The selected Google Business Profile does not have a website listed.",
                        suggestion: "Domain verification cannot be performed. Please add a website to your Google profile or contact support."
                    });
                    setPlaceDetails(null); // Ensure confirmation box does not show
                    setIsLoading(false); // Stop loading
                    return; // Stop execution
                }


                // --- THE CORE DOMAIN CHECK LOGIC ---
                // We check if a website domain exists and if it does NOT match the user's email domain.
                // 2. Compare domains.
                if (userEmailDomain !== businessWebsiteDomain) {
                    setError({
                        title: "Domain Mismatch",
                        details: `Your registered email domain ('${userEmailDomain}') does not match the business's website domain ('${businessWebsiteDomain}').`,
                        suggestion: "Please contact support if you believe this is an error."
                    });
                    setPlaceDetails(null);
                } else {
                    // 3. Success! Domains match. Proceed.
                    setPlaceDetails({
                        name: details.name,
                        rating: details.rating,
                        reviewCount: details.userRatingsTotal,
                        website: businessWebsite,
                        placeId: placeId
                    });
                }
            } else {
                setError({ title: "Error", details: details.message || 'Could not find details for this business.' });
            }
        } catch (err) {
            setError({ title: "API Error", details: err.response?.data?.Message || 'An error occurred while fetching details.' });
        } finally {
            setIsLoading(false);
        }
    };

const handleConfirmAndSave = async () => {
        if (!placeDetails) return;

        setIsLoading(true);
        setError(null); // Clear previous errors
        try {
            const payload = {
                businessId: businessId,
                googlePlaceId: placeDetails.placeId,
                googleRating: placeDetails.rating,
                googleRatingCount: placeDetails.reviewCount,
                googleWebsite: placeDetails.website
            };

            // Use your 'api' instance to call the backend
            await api.post('/api/BusinessProfile/update-google-rating', payload);
            
            // Tell the dashboard to refresh its data in the background
            onImportSuccess(); 
            // Switch the modal's view to the success screen
            setView('success');

        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to save Google Rating. Please try again.';
            setError({
                title: "Save Failed",
                details: errorMessage,
                suggestion: "Please try again or contact support."
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
            // Reset all local state to ensure the modal is fresh next time it opens
            setView('form'); 
            setSelectedPlace(null);
            setPlaceDetails(null);
            setError(null);
            setIsLoading(false);
            onClose();
        };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button className={styles.closeButton} onClick={handleClose}>&times;</button>
                
                {/* --- Conditionally render based on the 'view' state --- */}

                {view === 'form' && (
                    <>
                        <h3>Import Your Google Business Rating</h3>
                        <p className={styles.subtitle}>
                            Boost your profile's credibility by showcasing your existing Google reviews.
                        </p>
                        <div className={styles.infoBox}>
                            <FaInfoCircle />
                            <span>This helps homeowners trust your business from day one, leading to more quotes.</span>
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="googleBusinessSearch">Search for your Business on Google</label>
                            <GooglePlacesAutocomplete
                                apiKey={GOOGLE_PLACES_API_KEY}
                                selectProps={{
                                    value: selectedPlace,
                                    onChange: handlePlaceSelect,
                                    placeholder: 'Enter your business name and location...',
                                    isClearable: true,
                                }}
                            />
                        </div>
                        {isLoading && <p className={styles.loadingText}>Verifying details...</p>}
                        {placeDetails && !error && (
                            <div className={styles.confirmationBox}>
                                <h4>Is this your business?</h4>
                                <p className={styles.businessName}>{placeDetails.name}</p>
                                <p className={styles.websiteDisplay}>{placeDetails.website || "No website listed"}</p>
                                <div className={styles.ratingDisplay}>
                                   <FaStar />
                                   <strong>{placeDetails.rating || 'N/A'}</strong>
                                   <span>({placeDetails.reviewCount || 0} reviews)</span>
                                </div>
                                <button 
                                    className={styles.confirmButton} 
                                    onClick={handleConfirmAndSave}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : 'Yes, Import This Rating'}
                                </button>
                            </div>
                        )}
                        {error && (
                            <div className={styles.errorBox}>
                                <FaExclamationTriangle className={styles.errorIcon} />
                                <div>
                                    <strong>{error.title}</strong>
                                    <p className={styles.errorDetails}>{error.details}</p>
                                    {error.suggestion && <p className={styles.errorSuggestion}>{error.suggestion}</p>}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {view === 'success' && (
                    <div className={styles.successView}>
                        <div className={styles.successIconWrapper}>
                            <FaCheckCircle />
                        </div>
                        <h3>Success!</h3>
                        <p className={styles.subtitle}>
                            Your Google Rating has been imported and is now visible on your profile.
                        </p>
                        <button 
                            className={styles.confirmButton} 
                            onClick={handleClose}
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImportGoogleRatingModal;